/* tslint:disable:max-line-length */
import {Injectable, NgModuleRef, OnDestroy} from '@angular/core';
import {
  AppContainerOptions,
  AppContainerService,
  AppLeftPaneService,
  AppRightPaneService,
  ContentSideNavButton,
  LinkSideNavButton,
  PersonalizationService,
  RightDrawerContent,
  SideNavBarService,
  SideNavButton,
} from '@mydocma/app-container';
import {MmProjectService} from '../mm-project/mm-project.service';
import {Project as MmProject} from '@docma-mm-core/project/project';
import {MmModule} from '../mm.module';
import {MmRoutingService} from '../mm-routing/mm-routing.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActivatedRoute, Router} from '@angular/router';
import {MmSideNavBarButtonsEnum} from './mm-sidenavbar-buttons.enum';
import {MmProjectPermissionsService} from '../mm-project/mm-project-permissions-service';
import {ProjectUserPermissions} from '@docma-mm-core/permission/project-user-permissions';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, firstValueFrom, map} from 'rxjs';
import {filter, first, switchMap, tap} from 'rxjs/operators';
import {MmTaskDashboardComponent} from '../mm-components/app-container/mm-task-dashboard/mm-task-dashboard.component';
import {UserSettingsMenuItem} from '@mydocma/app-container/lib/user-menu/user-menu-items/user-settings/user-settings-menu-item';
import {buttonsMap, idToTranslationKey, idToUrlMap, leftPaneContentMap, urlToButtonMap} from './mm-sidnavbar-buttons.maps';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {MmFaultRightPaneService} from '../mm-faults/mm-faults-view/mm-fault-right-pane/mm-fault-right-pane.service';
import {MmLayoutStateService} from '../mm-filter/layout/layout-state.service';
import {CoPermissionArea, CoPermissionsService, CoPermissionType} from '@mydocma/co';
import {ConfigProvider} from '@mydocma/common';
import {IMmConfig} from '@docma-mm-core/project/project.interface';
import {
  CapacitorAppSettingsMenuItem
} from '@mydocma/app-container/lib/user-menu/user-menu-items/capacitor-app-settings/capacitor-app-settings-menu-item';
import {PlatformModeStateService} from '../mm-offline/services/platform-mode-state.service';
import {OfflineLocalModeMessageService} from '../mm-offline/services/offline-local-mode-message.service';
import {ProjectService} from '@mydocma/project';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {MmProjectLoaderService} from '../mm-project/mm-project-loader.service';


@UntilDestroy()
@Injectable()
export class MmSideNavBarService implements OnDestroy {


  private buttons: SideNavButton[] = [];
  private currentProject: MmProject;
  private fistProjectChanged = new BehaviorSubject<boolean>(false);
  private currentRoute: string;
  private appContainerOptions: AppContainerOptions;

  isleftPaneOpened = false;
  private userPermissions: ProjectUserPermissions;

  private bpPermisions: any;
  private defectPoolsVisible = false;
  private costsVisible = false;
  private invoicesVisible = false;

  private leftPaneContentMap = leftPaneContentMap;
  private buttonsMap = buttonsMap;
  private urlToButtonMap = urlToButtonMap;
  private idToUrlMap = idToUrlMap;
  private idToTranslationKey = idToTranslationKey;


  private lastOpenedContentButton: ContentSideNavButton;
  private lastContentButton: ContentSideNavButton;
  private contentClosedFromCode = false;

  private isSideNavOpen = false;
  private isRightPaneOpen = false;
  private isLeftPaneOpen = false;

  private isInOverviewComponent = false;
  private resizeListenerAttached = false;
  private isInitialized = false;

  private tooltipCloseTimers: Map<string, any> = new Map();

  /* USE FIELD-NAME FOR THESE FIELDS */
  private propToIdDict: {[prop: string]: string} = {
    ProjectSpecific_Name2: MmSideNavBarButtonsEnum.ProjectSettingsUserdefineds1,
    ProjectSpecific2_Name2: MmSideNavBarButtonsEnum.ProjectSettingsUserdefineds2
  };

  fieldNamesByIdSubject = new BehaviorSubject<{[id: string]: string}>(null);
  public fieldNamesById$ = this.fieldNamesByIdSubject.asObservable();

  constructor(private readonly projectService: MmProjectService,
              private readonly mydocmaProjectService: ProjectService,
              private readonly translateService: TranslateService,
              private readonly appContainerService: AppContainerService,
              private readonly leftPaneService: AppLeftPaneService,
              private readonly sideNavBarService: SideNavBarService,
              private readonly permissionsService: MmProjectPermissionsService,
              private readonly routingService: MmRoutingService,
              private readonly moduleRef: NgModuleRef<MmModule>,
              private readonly deviceService: DeviceService,
              private readonly faultRightPaneService: MmFaultRightPaneService,
              private readonly appRightPaneService: AppRightPaneService,
              private readonly layoutStateService: MmLayoutStateService,
              private readonly coPermissionsService: CoPermissionsService,
              private readonly personalizationService: PersonalizationService,
              private readonly router: Router,
              private configService: ConfigProvider<IMmConfig>,
              private readonly platformModeStateService: PlatformModeStateService,
              private readonly offlineLocalModeMessageService: OfflineLocalModeMessageService,
              private readonly projectLoaderService: MmProjectLoaderService) {


    this.leftPaneContentMap.forEach(e => e.moduleRef = this.moduleRef);
    this.initButtons();
    this.translateButtons(); // Force translation after button init

    this.sideNavBarService.connected
      .pipe(
        switchMap(c => this.appContainerService.mobileView$),
        switchMap(ex => this.permissionsService.permissions$.pipe(tap(ps => this.userPermissions = ps))),
        switchMap(pr => this.permissionsService.permissionDefectPoolsVisible$.pipe(tap(visible => {
          this.defectPoolsVisible = visible;
        }))),
        switchMap(pr => this.permissionsService.permissionCostsVisible$.pipe(tap(visible => {
          this.costsVisible = visible;
        }))),
        switchMap(pr => this.permissionsService.permissionInvoicesVisible$.pipe(tap(visible => {
          this.invoicesVisible = visible;
        }))),
        switchMap(pr => this.coPermissionsService.projectConfigPermissions$.pipe(tap(ps => this.bpPermisions = ps))),
        switchMap(pr => this.routingService.moduleActivatedRoute$.pipe(tap(ar => {
          this.onRouteChanged(ar);
          this.translateButtons();
        }))),
        switchMap(r => this.sideNavBarService.expanded$),
        untilDestroyed(this))
      .subscribe(value => {
        try {
          this.refreshButtons();
        } catch (ex) {
          console.error('refreshButtons failed', ex);
        }
      });

    this.leftPaneService.connected$
      .pipe(
        filter(Boolean),
        switchMap(c => this.fistProjectChanged.pipe(filter(Boolean))),
        switchMap(pr => this.routingService.moduleActivatedRoute$
          .pipe(
            filter(ar => !!(ar && this.routingService.getRoutePath(ar.snapshot))),
            first()
          )),
        untilDestroyed(this)
      )
      .subscribe(f => this.onFirstProjectChanged());


    this.projectService.currentProject
      .pipe(untilDestroyed(this))
      .subscribe(p => this.onProjectChanged(p));

    this.appContainerService.isLeftPaneOpen
      .pipe(untilDestroyed(this))
      .subscribe(isOpen => {
        this.isleftPaneOpened = isOpen;
      });

    this.appContainerService.activeContentButton
      .pipe(untilDestroyed(this))
      .subscribe(async clickedBtn => {
        this.lastContentButton = clickedBtn;
        if (!this.contentClosedFromCode) {
          this.lastOpenedContentButton = clickedBtn;
        }

        const btnInfo = this.urlToButtonMap[this.currentRoute];

        if (clickedBtn) {
          // Handle BackToProject buttons for both ProjectOverview and Costs
          if (clickedBtn.id === MmSideNavBarButtonsEnum.ProjectOverviewBackToProject) {
            [MmSideNavBarButtonsEnum.MainGroup, MmSideNavBarButtonsEnum.ProjectOverviewGroup, MmSideNavBarButtonsEnum.UserSettingsGroup, MmSideNavBarButtonsEnum.ProjectSettingsGroup].forEach(groupId => {
              this.getButtonsByGroupId(groupId).forEach(btn => btn.button.hidden = false);
            });
            this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectOverviewGroup).forEach(btn => btn.button.hidden = true);
            this.routingService.navigate([this.idToUrlMap.get(MmSideNavBarButtonsEnum.Faults)]);
            return;
          }
          if (clickedBtn.id === MmSideNavBarButtonsEnum.CostsBackToProject) {
            [MmSideNavBarButtonsEnum.MainGroup, MmSideNavBarButtonsEnum.ProjectOverviewGroup, MmSideNavBarButtonsEnum.UserSettingsGroup, MmSideNavBarButtonsEnum.ProjectSettingsGroup].forEach(groupId => {
              this.getButtonsByGroupId(groupId).forEach(btn => btn.button.hidden = false);
            });
            // Hide all CostsGroup buttons (Back, TimeTracking, Invoices, etc.)
            this.getButtonsByGroupId(MmSideNavBarButtonsEnum.CostsGroup).forEach(btn => {
              btn.button.hidden = true;
            });
            this.sideNavBarService.refreshNavbar();

            // Navigate back to main sidebar - try to go to current project's faults view
            // If we have a current project, navigate to its faults, otherwise fallback to faults route
            if (this.currentProject && this.currentProject.Id) {
              const targetUrl = `/${this.currentProject.Id}/faults`;
              this.routingService.navigate([targetUrl]);
            } else {
              // Fallback to the configured route
              const fallbackUrl = this.idToUrlMap.get(MmSideNavBarButtonsEnum.Faults);
              this.routingService.navigate([fallbackUrl]);
            }
            return;
          }

          if (clickedBtn.id === MmSideNavBarButtonsEnum.FilterStructure &&
            (btnInfo.content === MmSideNavBarButtonsEnum.ProjectSettingsPlans ||
              btnInfo.content === MmSideNavBarButtonsEnum.ProjectSettingsBimModels ||
              btnInfo.content === MmSideNavBarButtonsEnum.ProjectSettingsBimSections)) {
            return;
          }

          switch (btnInfo?.group) {
            case MmSideNavBarButtonsEnum.ProjectSettingsGroup:
            case MmSideNavBarButtonsEnum.UserSettingsGroup:
            case MmSideNavBarButtonsEnum.CapacitorAppSettings:
              this.routingService.navigate([this.idToUrlMap.get(MmSideNavBarButtonsEnum.Faults)]);
              break;
            case MmSideNavBarButtonsEnum.CostsGroup:
              [MmSideNavBarButtonsEnum.MainGroup, MmSideNavBarButtonsEnum.ProjectOverviewGroup, MmSideNavBarButtonsEnum.UserSettingsGroup, MmSideNavBarButtonsEnum.ProjectSettingsGroup].forEach(groupId => {
                this.getButtonsByGroupId(groupId).forEach(btn => btn.button.hidden = true);
              });
              this.getButtonsByGroupId(MmSideNavBarButtonsEnum.CostsGroup).forEach(btn => btn.button.hidden = false);
              const mydocmaProject = await firstValueFrom(this.mydocmaProjectService.currentProject);
              if (mydocmaProject?.customerId) {
                this.routingService.navigate(['customers', mydocmaProject.customerId, 'costs']);
              } else {
                console.warn('[MM-SideNav] No project or customerId available for Kosten navigation');
              }
              break;
          }
        }
      });

    this.appContainerService.notifyChanges(appcontainer => {
      const userMenuItem = appcontainer.header.userMenuItems.items.find(x => x.id == 'userSettings') as UserSettingsMenuItem;
      userMenuItem.click = async () => {
        const url = this.idToUrlMap.get(MmSideNavBarButtonsEnum.UserSettingsVariables);
        if (url) {
          this.routingService.navigate([url]);
        }
      };
      const capacitorAppSettingsMenuItem = appcontainer.header.userMenuItems.items.find(x => x.id == 'capacitorAppSettings') as CapacitorAppSettingsMenuItem;
      capacitorAppSettingsMenuItem.hidden = !this.configService.getConfigData().localModeConfig?.enabled;
      capacitorAppSettingsMenuItem.click = async () => {
        const url = this.idToUrlMap.get(MmSideNavBarButtonsEnum.CapacitorAppSettings);
        if (url) {
          this.routingService.navigate([url]);
        }
      };
    });

    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(l => this.translateButtons());

    this.setupResizeListener();
    this.isInitialized = true;

    this.platformModeStateService.isLocalMode$.subscribe(() => {
      this.customizeButtonsForLocalMode();
    });
  }

  private getButtonsByGroupId(groupId: MmSideNavBarButtonsEnum) {
    return Object.values(this.buttonsMap).filter(x => x.group === groupId);
  }

  private onRouteChanged(ar: ActivatedRoute) {
    this.currentRoute = ar && this.routingService.getRoutePath(ar.snapshot);

    let btnInfo = this.urlToButtonMap[this.currentRoute];

    if (!btnInfo && this.currentRoute) {
      if (this.currentRoute.match(/^\/projects\/[^\/]+\/costs(\/invoices)?$/) ||
        this.currentRoute.match(/^\/customers\/[^\/]+\/costs(\/invoices)?$/)) {
        btnInfo = {group: MmSideNavBarButtonsEnum.CostsGroup, content: MmSideNavBarButtonsEnum.Costs};
      }
    }

    if (btnInfo) {
      switch (btnInfo.group) {
        case MmSideNavBarButtonsEnum.CostsGroup:
        case MmSideNavBarButtonsEnum.ProjectOverviewGroup:
          this.closeContentSideNavFormCode();
          this.isInOverviewComponent = true;
          if (this.deviceService.isMobil()) {
            this.hideSideNavElements();
          }
          break;

        case MmSideNavBarButtonsEnum.ProjectSettingsGroup:
        case MmSideNavBarButtonsEnum.UserSettingsGroup:
        case MmSideNavBarButtonsEnum.CapacitorAppSettings:
          if (btnInfo.content == MmSideNavBarButtonsEnum.ProjectSettingsPlans ||
            btnInfo.content == MmSideNavBarButtonsEnum.ProjectSettingsBimModels ||
            btnInfo.content == MmSideNavBarButtonsEnum.ProjectSettingsBimSections) {
            if (this.lastOpenedContentButton && this.lastOpenedContentButton.id == MmSideNavBarButtonsEnum.FilterLayout) {
              const btn = this.buttonsMap[MmSideNavBarButtonsEnum.FilterStructure].button as ContentSideNavButton;
              this.appContainerService.openContentSideNav(btn.id, btn.content);
            }
          } else {
            this.closeContentSideNavFormCode();
          }
          this.isInOverviewComponent = false;
          this.showSideNavElements();
          break;

        case MmSideNavBarButtonsEnum.MainGroup:
          if (this.lastOpenedContentButton && !this.isleftPaneOpened) {
            this.appContainerService.openContentSideNav(this.lastOpenedContentButton.id, this.lastOpenedContentButton.content);
          }
          this.isInOverviewComponent = false;
          this.showSideNavElements();
          break;
      }
    }

    this.setActiveLinkButton(this.currentRoute, ar?.snapshot?.queryParams);
  }

  private closeContentSideNavFormCode() {
    this.contentClosedFromCode = true;
    this.appContainerService.closeContentSideNav();
    this.contentClosedFromCode = false;
  }

  private async refreshButtons() {
    let btnInfo = this.urlToButtonMap[this.currentRoute];

    // If exact match fails, try pattern matching for dynamic routes
    if (!btnInfo && this.currentRoute) {
      // Check for project-based invoice route pattern
      if (this.currentRoute.match(/^\/projects\/[^\/]+\/costs\/invoices$/)) {
        btnInfo = {group: MmSideNavBarButtonsEnum.CostsGroup, content: MmSideNavBarButtonsEnum.Invoices};
      }
      // Check for customer-based invoice route pattern
      else if (this.currentRoute.match(/^\/customers\/[^\/]+\/costs\/invoices$/)) {
        btnInfo = {group: MmSideNavBarButtonsEnum.CostsGroup, content: MmSideNavBarButtonsEnum.Invoices};
      }
      // Check for project-based costs route pattern
      else if (this.currentRoute.match(/^\/projects\/[^\/]+\/costs$/)) {
        btnInfo = {group: MmSideNavBarButtonsEnum.CostsGroup, content: MmSideNavBarButtonsEnum.Costs};
      }
      // Check for customer-based costs route pattern
      else if (this.currentRoute.match(/^\/customers\/[^\/]+\/costs$/)) {
        btnInfo = {group: MmSideNavBarButtonsEnum.CostsGroup, content: MmSideNavBarButtonsEnum.Costs};
      }
    }

    const isBarOpened = this.sideNavBarService.isBarOpened();
    const isMobil = this.appContainerService.isMobileView;
    let hasSomeProjectSettings = false;

    if (btnInfo) {
      if (btnInfo.group === MmSideNavBarButtonsEnum.UserSettingsGroup) {
        // refresh  user settings sidenavbar btns
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.FilterGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectOverviewGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.CapacitorAppSettings).forEach(btn => {
          btn.button.hidden = true;
        });


        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.UserSettingsGroup).forEach(btn => {
          btn.button.hidden = btn.button.id === MmSideNavBarButtonsEnum.UserSettingsGroup ? !isBarOpened : false;
          if (btn.button.id !== MmSideNavBarButtonsEnum.UserSettingsBackToProject &&
            btn.button.id != MmSideNavBarButtonsEnum.UserSettingsGroup) {
            if (!isBarOpened) {
              btn.button.parentId = undefined;
            } else {
              btn.button.parentId = MmSideNavBarButtonsEnum.UserSettingsGroup;
            }
            btn.button.hidden = false;
            btn.hasPermission = true;
          }

        });

      } else if (btnInfo.group === MmSideNavBarButtonsEnum.CapacitorAppSettings) {
        // refresh capacitor app settings sidenavbar btns
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.FilterGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectOverviewGroup).forEach(btn => {
          btn.button.hidden = true;
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.UserSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.CapacitorAppSettings).forEach(btn => {
          btn.button.hidden = false;
          if (btn.button.id !== MmSideNavBarButtonsEnum.CapacitorAppSettingsBackToProject) {
            btn.button.parentId = undefined;
            btn.hasPermission = true;
          }
        });

      } else if (btnInfo.group === MmSideNavBarButtonsEnum.ProjectOverviewGroup) {
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.FilterGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.UserSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectOverviewGroup).forEach(btn => {
          btn.button.hidden = false;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.CapacitorAppSettings).forEach(btn => {
          btn.button.hidden = true;
        });
      } else if (btnInfo.group === MmSideNavBarButtonsEnum.CostsGroup) {
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.FilterGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.UserSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectOverviewGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.CostsGroup).forEach(btn => {
          btn.button.hidden = false;
        });

      } else if (btnInfo.group === MmSideNavBarButtonsEnum.ProjectSettingsGroup) {

        const mainGroupButtons = this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup);
        const filterChildren = mainGroupButtons.filter(btn => btn.button.parentId === MmSideNavBarButtonsEnum.FilterGroup);
        filterChildren.forEach(btn => {
          if (btn.button.id === MmSideNavBarButtonsEnum.FilterPool) {
            btn.button.hidden = !isBarOpened || !this.defectPoolsVisible;
          } else {
            btn.button.hidden = !isBarOpened;
          }
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.FilterGroup).forEach(btn => {
          btn.button.hidden = !isBarOpened;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup).forEach(btn => {
          btn.button.hidden = !isBarOpened;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectOverviewGroup).forEach(btn => {
          btn.button.hidden = !isBarOpened || btn.button.id == MmSideNavBarButtonsEnum.ProjectOverviewBackToProject;
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.UserSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.CapacitorAppSettings).forEach(btn => {
          btn.button.hidden = true;
        });


        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectSettingsGroup).forEach(btn => {
          switch (btn.button.id) {
            case MmSideNavBarButtonsEnum.ProjectSettingsBackToMain:
              btn.button.hidden = isBarOpened || isMobil;
              btn.button.parentId = undefined;
              break;
            case MmSideNavBarButtonsEnum.ProjectSettingsGroup:
              btn.button.hidden = !isBarOpened || isMobil;
              break;
            default:
              const perm = this.hasConfigPermission(this.userPermissions, btn.button);
              btn.button.parentId = isBarOpened ? MmSideNavBarButtonsEnum.ProjectSettingsGroup : undefined;
              btn.button.hidden = (isMobil || !perm);
              btn.hasPermission = perm;
              hasSomeProjectSettings = perm || hasSomeProjectSettings;
          }
        });


      } else if (btnInfo.group === MmSideNavBarButtonsEnum.MainGroup) {


        const mainGroupButtons = this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup);
        const filterChildren = mainGroupButtons.filter(btn => btn.button.parentId === MmSideNavBarButtonsEnum.FilterGroup);

        filterChildren.forEach(btn => {
          if (btn.button.id === MmSideNavBarButtonsEnum.FilterPool) {
            btn.button.hidden = !this.defectPoolsVisible;
          } else {
            btn.button.hidden = false;
          }
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.FilterGroup).forEach(btn => {
          btn.button.hidden = false;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup).forEach(btn => {
          if (btn.button.id === MmSideNavBarButtonsEnum.FilterPool && btn.button.parentId === MmSideNavBarButtonsEnum.FilterGroup) {
          } else {
            btn.button.hidden = false;
          }
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectOverviewGroup).forEach(btn => {
          btn.button.hidden = btn.button.id == MmSideNavBarButtonsEnum.ProjectOverviewBackToProject;
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.UserSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.CapacitorAppSettings).forEach(btn => {
          btn.button.hidden = true;
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectSettingsGroup).forEach(btn => {
          switch (btn.button.id) {
            case MmSideNavBarButtonsEnum.ProjectSettingsBackToMain:
              btn.button.hidden = true;
              break;
            case MmSideNavBarButtonsEnum.ProjectSettingsGroup:
              btn.button.hidden = isMobil;
              break;
            default:
              const perm = this.hasConfigPermission(this.userPermissions, btn.button);
              btn.button.parentId = isBarOpened ? MmSideNavBarButtonsEnum.ProjectSettingsGroup : undefined;
              btn.button.hidden = (!isBarOpened || isMobil || !perm);
              btn.hasPermission = perm;
              hasSomeProjectSettings = perm || hasSomeProjectSettings;
          }
        });
      }

      const plansBtn = this.buttonsMap[MmSideNavBarButtonsEnum.Plans];
      if (!plansBtn.button.hidden) {
        plansBtn.button.hidden = !isMobil;
      }

      const bpModelsBtn = this.buttonsMap[MmSideNavBarButtonsEnum.ProjectSettingsBimModels];
      if (!bpModelsBtn.button.hidden) {
        bpModelsBtn.button.hidden = isMobil || !this.coPermissionsService.hasProjectPermission(CoPermissionArea.BimModels, CoPermissionType.Visible);
      }

      const structuresBtn = this.buttonsMap[MmSideNavBarButtonsEnum.ProjectSettingsStructures];
      if (!structuresBtn.button.hidden) {
        structuresBtn.button.hidden = isMobil;
      }

      const bpSectionsBtn = this.buttonsMap[MmSideNavBarButtonsEnum.ProjectSettingsBimSections];
      if (!bpSectionsBtn.button.hidden) {
        bpSectionsBtn.button.hidden = isMobil || !this.coPermissionsService.hasProjectPermission(CoPermissionArea.BimSections, CoPermissionType.Visible);
      }

      const bpPlansBtn = this.buttonsMap[MmSideNavBarButtonsEnum.ProjectSettingsPlans];
      if (!bpPlansBtn.button.hidden) {
        bpPlansBtn.button.hidden = isMobil || !this.coPermissionsService.hasProjectPermission(CoPermissionArea.Plans, CoPermissionType.Visible);
      }

      const settingsGroupBtn = this.buttonsMap[MmSideNavBarButtonsEnum.ProjectSettingsGroup];
      if (!settingsGroupBtn.button.hidden) {
        settingsGroupBtn.button.hidden = !hasSomeProjectSettings;
      }

      const isMobileOrTablet = this.deviceService.isMobil() || this.deviceService.isTablet();

      const costsBtn = this.buttonsMap[MmSideNavBarButtonsEnum.Costs];
      if (costsBtn && !costsBtn.button.hidden) {
        costsBtn.button.hidden = !this.costsVisible || isMobileOrTablet;
      }

      const invoicesBtn = this.buttonsMap[MmSideNavBarButtonsEnum.Invoices];
      if (invoicesBtn && btnInfo.group === MmSideNavBarButtonsEnum.CostsGroup) {
        invoicesBtn.button.hidden = !this.invoicesVisible || isMobileOrTablet;
      }

      this.translateButtons();
      this.sideNavBarService.refreshNavbar();

    } else if (this.currentRoute) {
      // console.error('No button found for url' + this.currentRoute);
    }

    // Final guard: Always hide CostsGroup buttons unless current group is CostsGroup
    if (!btnInfo || btnInfo.group !== MmSideNavBarButtonsEnum.CostsGroup) {
      this.getButtonsByGroupId(MmSideNavBarButtonsEnum.CostsGroup).forEach(btn => {
        btn.button.hidden = true;
      });
    }

    if (btnInfo?.group === MmSideNavBarButtonsEnum.CostsGroup || btnInfo?.group === MmSideNavBarButtonsEnum.ProjectOverviewGroup) {
      [MmSideNavBarButtonsEnum.ProjectSettingsGroup, MmSideNavBarButtonsEnum.CapacitorAppSettings].forEach(groupId => {
        this.getButtonsByGroupId(groupId).forEach(btn => {
          btn.button.hidden = true;
        });
      });
    }
  }

  ngOnDestroy(): void {
    if (this.resizeListenerAttached) {
      window.removeEventListener('resize', this.handleWindowResizeForOverviewComponent);
    }

    this.tooltipCloseTimers.forEach(timer => clearTimeout(timer));
    this.tooltipCloseTimers.clear();
  }

  private setActiveLinkButton(path: string, queryParams?: { [key: string]: any }) {
    if (!path) {
      return;
    }

    const pathInternal = path;

    let activeBtn = (this.urlToButtonMap.hasOwnProperty(pathInternal) ? this.urlToButtonMap[pathInternal] : null);
    if (!activeBtn) {
      return;
    }
    if (this.appContainerService.isMobileView) {
      if (activeBtn.content === MmSideNavBarButtonsEnum.Faults && queryParams?.view === 'plan') {
        activeBtn = {content: MmSideNavBarButtonsEnum.Plans, group: MmSideNavBarButtonsEnum.MainGroup};
      }
    }
    this.buttons.forEach(b => {
      if (b instanceof LinkSideNavButton && activeBtn.content === b.id) {
        b.active = true;
        // console.warn(`(6) SET ${(b.active ? 'ACTIVE' : 'INACTIVE')}: ${b.id}`);
      } else {
        b.active = false;
      }
    });
    // toggle structure button based on if it has been selected
    if (this.lastContentButton?.id === MmSideNavBarButtonsEnum.FilterStructure) {
      this.buttonsMap[MmSideNavBarButtonsEnum.FilterStructure].button.active = true;
    } else {
      this.buttonsMap[MmSideNavBarButtonsEnum.FilterStructure].button.active = false;
    }
    if (this.lastContentButton?.id === MmSideNavBarButtonsEnum.FilterLayout) {
      this.buttonsMap[MmSideNavBarButtonsEnum.FilterLayout].button.active = true;
    }
  }

  private translateButtons() {
    this.idToTranslationKey.forEach((key, id) => {
      const btn = this.buttonsMap[id];
      const value = (this.fieldNamesByIdSubject.value?.hasOwnProperty(id) &&  this.fieldNamesByIdSubject.value[id] != null && this.fieldNamesByIdSubject.value[id] !== '' ? this.fieldNamesByIdSubject.value[id] : this.translateService.instant(key));

      const setTextAndTooltip = (translated) => {
        if (btn) {
          btn.button.text = translated;
          if (btn.button.id === MmSideNavBarButtonsEnum.ProjectSettingsBackToMain){
            btn.button.tooltip = '';
          } else{
            btn.button.tooltip = translated;
          }
        }
      };

      if ((!this.fieldNamesByIdSubject?.value || !this.fieldNamesByIdSubject?.value?.hasOwnProperty(id)) && value === key) {
        // Fallback: async load if not ready
        this.translateService.get(key).subscribe(translated => setTextAndTooltip(translated));
      } else {
        setTextAndTooltip(value);
      }
    });
  }


  private async onProjectChanged(p: MmProject) {
    this.currentProject = p;

    if (this.currentProject && !this.fistProjectChanged.value) {
      setTimeout(() => {
        this.fistProjectChanged.next(true);
      }, 300);
    }

    this.projectLoaderService.fields
      .pipe(
        filter(f => f.loadState.status === LoadStatusEnum.Loaded),
        map(m => {
          return m.data.filter(x => x.Id != null && this.propToIdDict.hasOwnProperty(x.Id)).map(x => [this.propToIdDict[x.Id], (x.Project ?? x.Global)]);
        }))
      .subscribe(fields => {
        this.fieldNamesByIdSubject.next(Object.fromEntries(fields));
        this.translateButtons();
      });

    if (this.deviceService.isMobil() && !this.deviceService.isTablet()) {
      this.appContainerService.closeContentSideNav();
      this.sideNavBarService.collapseBar();
      this.sideNavBarService.openBar();
    }

    if (this.deviceService.isTablet()) {
      await this.onFirstProjectChanged();
      this.sideNavBarService.collapseBar();
      this.sideNavBarService.openBar();
    }
  }

  private async onFirstProjectChanged() {
    // Show structure after first project selected
    const btnInfo = this.urlToButtonMap[this.currentRoute];
    // MMW-3599
    // const sideNavBarSelectedFilter = await this.personalizationService.getValue('mm_SideNavBar_SelectedFilter');
    // if (this.deviceService.isMobil()  && btnInfo?.group === MmSideNavBarButtonsEnum.MainGroup && (sideNavBarSelectedFilter === MmSideNavBarButtonsEnum.FilterStructure || sideNavBarSelectedFilter === MmSideNavBarButtonsEnum.FilterLayout)) {
    //   setTimeout(() => {
    //     const btn = this.buttonsMap[sideNavBarSelectedFilter].button as ContentSideNavButton;
    //     this.appContainerService.openContentSideNav(btn.id, btn.content);
    //   }, 100);
    // }
    // else

    if (!this.deviceService.isMobil() && btnInfo?.group === MmSideNavBarButtonsEnum.MainGroup) {
      setTimeout(() => {
        if (!this.layoutStateService.defaultLayoutHasNoFields) {
          const btn = this.buttonsMap[MmSideNavBarButtonsEnum.FilterStructure].button as ContentSideNavButton;
          this.appContainerService.openContentSideNav(btn.id, btn.content);
        }
      }, 100);
    }

  }

  private hasConfigPermission(ps: ProjectUserPermissions, button: SideNavButton) {

    if (!ps?.Configs) {
      return false;
    }
    return this.permissionsService.hasRoutePermissions(ps, this.idToUrlMap.get(button.id));
  }

  register(appContainer: AppContainerOptions) {
    this.appContainerOptions = appContainer;
    this.leftPaneService.setLeftPaneFlexBasisPx(400);
    appContainer.sideNavBar.buttons = this.buttons;
    this.appContainerOptions.rightDrawer.contents = [
      new RightDrawerContent({
        componentType: MmTaskDashboardComponent,
        id: 'task-dashboard',
        moduleRef: this.moduleRef,
        autoFocus: false
      }),
    ];

  }

  unregister(appContainer: AppContainerOptions) {
    appContainer.sideNavBar.buttons = [];
    this.appContainerOptions = null;
  }

  public getUrl(btnId: string) {
    const url = this.idToUrlMap.get(btnId);
    return url;
  }

  private async onLinkButtonClick(btnId: string) {
    if (this.deviceService.isMobil() || this.deviceService.isTablet()) {
      const btn = this.buttonsMap[btnId];
      if (btn && btn.button) {
        if (this.tooltipCloseTimers.has(btnId)) {
          clearTimeout(this.tooltipCloseTimers.get(btnId));
        }

        const timer = setTimeout(() => {
          if (btn && btn.button) {
            btn.button.tooltip = '';
          }
          this.tooltipCloseTimers.delete(btnId);
        }, 1500);

        this.tooltipCloseTimers.set(btnId, timer);
      }
    }

    const url = this.idToUrlMap.get(btnId);
    const project = await firstValueFrom(this.mydocmaProjectService.currentProject);

    if (!project) {
      console.warn('[MM-SideNav] No project available for navigation, btnId:', btnId);
      return;
    }

    switch (btnId) {
      case MmSideNavBarButtonsEnum.Plans:
        this.routingService.navigate([url], {queryParams: {view: 'plan'}});
        break;
      case MmSideNavBarButtonsEnum.ProjectSettingsBackToMain:
        this.routingService.navigate([this.idToUrlMap.get(MmSideNavBarButtonsEnum.Faults).replace(':projectId', project.id)]);
        break;
      case MmSideNavBarButtonsEnum.ProjectSettingsGroup:
        if (!this.sideNavBarService.isBarOpened()) {
          const firstProjectSetting =
            this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectSettingsGroup)
              .find(x => x.button.id != MmSideNavBarButtonsEnum.ProjectSettingsBackToMain &&
                x.button.id != MmSideNavBarButtonsEnum.ProjectSettingsGroup &&
                x.hasPermission);
          if (firstProjectSetting) {
            this.routingService.navigate([this.idToUrlMap.get(firstProjectSetting.button.id).replace(':projectId', project.id)]);
          }
        }
        break;
      case MmSideNavBarButtonsEnum.Costs:
      case MmSideNavBarButtonsEnum.Invoices:
        if (project?.customerId) {
          this.routingService.navigate([`customers/${project.customerId}/costs/invoices`]);
        }
        break;
      default:
        if (url) {
          this.routingService.navigate([url.replace(':projectId', project.id)]);
        }
    }

  }

  private initButtons() {
    this.buttonsMap[MmSideNavBarButtonsEnum.FilterGroup].button.click.subscribe(b => {
      // this.onFilterGroupClick();
      setTimeout(() => {
        this.sideNavBarService.refreshNavbar();
      }, 100);
    });

    this.translateButtons();

    const dashboardEnabled = (this.configService.getConfigData().mmConfig.dashboardEnabled ?? true);
    if (!dashboardEnabled && this.buttonsMap.hasOwnProperty(MmSideNavBarButtonsEnum.ProjectOverview)) {
      delete this.buttonsMap[MmSideNavBarButtonsEnum.ProjectOverview];
    }

    for (const btn of Object.values(this.buttonsMap)) {
      this.buttons.push(btn.button);

      if (btn.button instanceof LinkSideNavButton) {
        btn.button.click.subscribe(async b => await this.onLinkButtonClick(btn.button.id));
      }
    }

    this.buttons.forEach(b => {
      b.hidden = true;
      if (b instanceof LinkSideNavButton) {
        b.active = false;
      }
    });
    const url = this.router.url || '';
    if (url.includes('/costs')) {
      this.getButtonsByGroupId(MmSideNavBarButtonsEnum.CostsGroup).forEach(btn => {
        btn.button.hidden = false;
      });
    } else if (url.includes('projects-overview')) {
      this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectOverviewGroup).forEach(btn => {
        btn.button.hidden = false;
      });
      const overviewBtn = this.buttonsMap[MmSideNavBarButtonsEnum.ProjectOverview]?.button;
      if (overviewBtn) {
        overviewBtn.active = true;
      }
    } else {
      const safeInitButtons = [
        MmSideNavBarButtonsEnum.FilterGroup,
        MmSideNavBarButtonsEnum.FilterStructure,
        MmSideNavBarButtonsEnum.FilterLayout,
        MmSideNavBarButtonsEnum.FilterPool,
        MmSideNavBarButtonsEnum.FilterFields,
        MmSideNavBarButtonsEnum.Faults
      ];
      this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup).forEach(btn => {
        btn.button.hidden = !safeInitButtons.includes(btn.button.id as MmSideNavBarButtonsEnum);
      });
      this.getButtonsByGroupId(MmSideNavBarButtonsEnum.FilterGroup).forEach(btn => {
        btn.button.hidden = false;
      });
      this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectOverviewGroup).forEach(btn => {
        btn.button.hidden = true;
      });
    }
    this.setActiveLinkButton(url.split('?')[0], this.router.parseUrl(url).queryParams);
  }

  private hideSideNavElements(): void {
    const navBarElement = document.querySelector('.nav-bar.is-mobile') as HTMLElement;
    if (navBarElement) {
      navBarElement.style.width = '0';
    }

    const menuButton = document.querySelector('button.menu-button') as HTMLButtonElement;
    if (menuButton) {
      menuButton.disabled = true;
    }

    const grabberButton = document.querySelector('button.nav-bar-grabber') as HTMLElement;
    if (grabberButton) {
      grabberButton.style.display = 'none';
    }
  }


  private showSideNavElements(): void {
    const navBarElement = document.querySelector('.nav-bar.is-mobile') as HTMLElement;
    if (navBarElement) {
      navBarElement.style.width = '';
    }

    const menuButton = document.querySelector('button.menu-button') as HTMLButtonElement;
    if (menuButton) {
      menuButton.disabled = false;
    }

    const grabberButton = document.querySelector('button.nav-bar-grabber') as HTMLElement;
    if (grabberButton) {
      grabberButton.style.display = '';
    }
  }

  private setupResizeListener(): void {
    if (this.resizeListenerAttached) {
      return;
    }

    this.resizeListenerAttached = true;

    window.addEventListener('resize', this.handleWindowResizeForOverviewComponent);
  }

  private handleWindowResizeForOverviewComponent = (): void => {
    const mobileViewBreakpoint = 600;
    if (!this.isInOverviewComponent) {
      return;
    }

    const windowWidth = window.innerWidth;

    const isMobileView = (windowWidth <= mobileViewBreakpoint && (this.deviceService.isMobil() || this.deviceService.isTablet()));
    if (isMobileView) {
      this.hideSideNavElements();
    } else {
      this.showSideNavElements();
    }
  }

  private customizeButtonsForLocalMode(): void {
    if (this.platformModeStateService.isLocalModeEnabled) {
      const dashboard = this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectOverviewGroup);
      dashboard.forEach(bnt => {
        if (bnt.button.id === MmSideNavBarButtonsEnum.ProjectOverview) {
          bnt.button.disabled = this.platformModeStateService.isLocalMode;
        }
      });
    }
  }

}
